/**
 * shared/pmove/mantle.ts — Mantle_Check / Mantle_Start / Mantle_Move / Mantle_CapView.
 *
 * OWNER: PMOVE agent.
 * T6 PM_CheckMantle 0x4d64c0 [PM §2]; dvars mantle_* from research/data/dvars_t6mp.json
 * (Mantle_RegisterDvars 0x5b1540): mantle_enable 1, mantle_check_range 20 / mantle_check_range_mp 10,
 * mantle_check_radius 0.1, mantle_check_angle 60, mantle_check_glass_extra_range 10,
 * mantle_view_yawcap 60, mantle_weapon_height 39, mantle_weapon_anim_height 32.
 * Logic: [T5 bg_mantle.cpp] (the T6 mantle code was not decompiled; the dvars and the anim set are
 * identical). Root motion: mantle.generated.ts (mp_mantle_up_* / mp_mantle_over_* xanim deltas).
 *
 * A mantle starts when you face a MANTLE-contents brush (surface flag mantle_on / mantle_over)
 * within range, a ledge 21..57 u up is free, and JUMP is held [T5 Mantle_CheckLedge].
 */
import { CONTENTS, createTraceResult, SURF } from '../collision/types';
import { ENTITYNUM_NONE, PLAYER_HEIGHT_CROUCH, PLAYER_MAXS_STAND, PLAYER_MINS } from '../constants';
import { dacos, dcosDeg, dsinDeg, RAD2DEG, type Vec3, vnormalize } from '../math';
import { Button, EF, PEv, PMF, PmType, type PlayerState } from '../types';
import { MANTLE_ANIMS, type MantleAnimDelta } from './mantle.generated';
import { angleDelta, angleNorm360, pmTrace, STANCE_MASK_CLEAR, traceWalkable, vecToYaw, type Pm } from './pm';

// ---- dvars (dvars_t6mp.json, Mantle_RegisterDvars 0x5b1540) ---------------------------------
export const MANTLE_ENABLE = true;
/** mantle_check_range_mp 10: the MP variant T6 added next to mantle_check_range 20. ESTIMATE that MP reads it. */
export const MANTLE_CHECK_RANGE_MP = 10;
export const MANTLE_CHECK_RADIUS = 0.1;
export const MANTLE_CHECK_ANGLE = 60;
export const MANTLE_CHECK_GLASS_EXTRA_RANGE = 10;
export const MANTLE_VIEW_YAWCAP = 60;
export const MANTLE_WEAPON_HEIGHT = 39;

/** ps.mantleFlags bits [T5 MantleState.flags] (only the u8 ones). */
export const MantleFlag = {
  OVER: 0x1, //          mantle over the obstacle (MANTLE_OVER surface)
  ON: 0x2, //            mantle onto it (MANTLE_ON surface)
  CROUCH_AT_LEDGE: 0x4, // a standing hull does not fit at the ledge
  STAND_AT_END: 0x8, //  a standing hull fits at the end position
  AVAILABLE: 0x10, //    HUD hint "press jump to mantle" (cleared every frame)
  MOUNT: 0x20,
  KEEP_WEAPON: 0x40, //  low mantle: weapon stays up (height <= mantle_weapon_height 39)
} as const;

/** s_mantleTrans [T5 bg_mantle.cpp]: { up anim, over anim, height }. */
const MANTLE_TRANS: readonly { up: number; over: number; height: number }[] = [
  { up: 1, over: 8, height: 57 }, { up: 2, over: 8, height: 51 }, { up: 3, over: 9, height: 45 },
  { up: 4, over: 9, height: 39 }, { up: 5, over: 9, height: 33 }, { up: 6, over: 10, height: 27 },
  { up: 7, over: 10, height: 21 },
];

function upLength(ps: PlayerState): number {
  return MANTLE_ANIMS[MANTLE_TRANS[ps.mantleTransIndex].up].lengthMs;
}
function overLength(ps: PlayerState): number {
  return ps.mantleFlags & MantleFlag.OVER ? MANTLE_ANIMS[MANTLE_TRANS[ps.mantleTransIndex].over].lengthMs : 0;
}
/** Mantle_GetDuration. */
export function mantleDuration(ps: PlayerState): number {
  return upLength(ps) + overLength(ps);
}

/** XAnimGetAbsDelta(anim, frac): root translation at `frac` of the anim (linear between keys). */
function animDelta(a: MantleAnimDelta, frac: number, out: Vec3): void {
  const t = frac * a.numFrames;
  const f = a.frames;
  const n = f.length;
  if (t <= f[0]) { out[0] = a.x[0]; out[1] = a.y[0]; out[2] = a.z[0]; return; }
  if (t >= f[n - 1]) { out[0] = a.x[n - 1]; out[1] = a.y[n - 1]; out[2] = a.z[n - 1]; return; }
  let i = 1;
  while (f[i] < t) i++;
  const k = (t - f[i - 1]) / (f[i] - f[i - 1]);
  out[0] = a.x[i - 1] + (a.x[i] - a.x[i - 1]) * k;
  out[1] = a.y[i - 1] + (a.y[i] - a.y[i - 1]) * k;
  out[2] = a.z[i - 1] + (a.z[i] - a.z[i - 1]) * k;
}

const upTotal: Vec3 = [0, 0, 0];
/** Mantle_GetAnimDelta [T5 bg_mantle.cpp]: world-space root delta at `time` ms into the mantle. */
function mantleAnimDelta(ps: PlayerState, time: number, out: Vec3): void {
  const up = upLength(ps);
  const over = overLength(ps);
  const tr = MANTLE_TRANS[ps.mantleTransIndex];
  if (time > up && over > 0) {
    animDelta(MANTLE_ANIMS[tr.up], 1, upTotal);
    animDelta(MANTLE_ANIMS[tr.over], (time - up) / over, out);
    out[0] += upTotal[0]; out[1] += upTotal[1]; out[2] += upTotal[2];
  } else {
    animDelta(MANTLE_ANIMS[tr.up], up > 0 ? Math.min(1, time / up) : 1, out);
  }
  // VectorAngleMultiply(delta, yaw)
  const c = dcosDeg(ps.mantleYaw), s = dsinDeg(ps.mantleYaw);
  const x = out[0], y = out[1];
  out[0] = x * c - y * s;
  out[1] = x * s + y * c;
}

/** Mantle_FindTransition: the anim whose height is closest to the ledge height. */
function findTransition(curHeight: number, goalHeight: number): number {
  const h = goalHeight - curHeight;
  let best = 0;
  let bestDiff = Math.abs(MANTLE_TRANS[0].height - h);
  for (let i = 1; i < MANTLE_TRANS.length; i++) {
    const d = Math.abs(MANTLE_TRANS[i].height - h);
    if (bestDiff > d) { best = i; bestDiff = d; }
  }
  return best;
}

// ---- scratch -------------------------------------------------------------------------------
const tr = createTraceResult();
const mDir: Vec3 = [0, 0, 0];
const startPos: Vec3 = [0, 0, 0];
const ledgePos: Vec3 = [0, 0, 0];
const endPos: Vec3 = [0, 0, 0];
const tStart: Vec3 = [0, 0, 0];
const tEnd: Vec3 = [0, 0, 0];
const mins: Vec3 = [0, 0, 0];
const maxs: Vec3 = [0, 0, 0];
const trans: Vec3 = [0, 0, 0];
const prevTrans: Vec3 = [0, 0, 0];
let resFlags = 0;

/** Mantle_FindMantleSurface [T5 bg_mantle.cpp]. */
function findMantleSurface(pm: Pm): boolean {
  const ps = pm.ps;
  const r = MANTLE_CHECK_RADIUS;
  mins[0] = -r; mins[1] = -r; mins[2] = 0;
  maxs[0] = r; maxs[1] = r; maxs[2] = PLAYER_MAXS_STAND[2];
  const innerDist = PLAYER_MAXS_STAND[0] - r;
  const traceDist = MANTLE_CHECK_RANGE_MP + innerDist;
  const dx0 = pm.forward[0], dy0 = pm.forward[1];
  const len = Math.sqrt(dx0 * dx0 + dy0 * dy0);
  if (!len) return false;
  const dx = dx0 / len, dy = dy0 / len;
  tStart[0] = ps.origin[0] - innerDist * dx; tStart[1] = ps.origin[1] - innerDist * dy; tStart[2] = ps.origin[2];
  tEnd[0] = ps.origin[0] + traceDist * dx; tEnd[1] = ps.origin[1] + traceDist * dy; tEnd[2] = ps.origin[2];
  pmTrace(pm, tr, tStart, mins, maxs, tEnd, CONTENTS.MANTLE);
  if (tr.startsolid || tr.allsolid) return false; // mantle brush too thick
  if (tr.fraction === 1) return false;
  if (!(tr.surfaceFlags & (SURF.MANTLE_ON | SURF.MANTLE_OVER | 0x10000000))) return false;
  mDir[0] = -tr.normal[0]; mDir[1] = -tr.normal[1]; mDir[2] = 0;
  if (vnormalize(mDir) < 0.000099999997) return false; // vertical normal
  const dot = Math.max(-1, Math.min(1, dx * mDir[0] + dy * mDir[1]));
  if (dacos(dot) * RAD2DEG > MANTLE_CHECK_ANGLE) return false; // not facing it
  resFlags = 0;
  if (tr.surfaceFlags & SURF.MANTLE_OVER) resFlags |= MantleFlag.OVER;
  else if (tr.surfaceFlags & SURF.MANTLE_ON) resFlags |= MantleFlag.ON;
  if (tr.surfaceFlags & 0x10000000) resFlags |= MantleFlag.MOUNT;
  return true;
}

/** Mantle_CalcEndPos [T5 bg_mantle.cpp]. */
function calcEndPos(pm: Pm): void {
  if (!(resFlags & MantleFlag.OVER)) {
    endPos[0] = ledgePos[0]; endPos[1] = ledgePos[1]; endPos[2] = ledgePos[2];
    return;
  }
  mins[0] = PLAYER_MINS[0]; mins[1] = PLAYER_MINS[1]; mins[2] = 0;
  maxs[0] = PLAYER_MAXS_STAND[0]; maxs[1] = PLAYER_MAXS_STAND[1]; maxs[2] = PLAYER_HEIGHT_CROUCH;
  tEnd[0] = ledgePos[0] + 31 * mDir[0]; tEnd[1] = ledgePos[1] + 31 * mDir[1]; tEnd[2] = ledgePos[2] + 31 * mDir[2];
  pmTrace(pm, tr, ledgePos, mins, maxs, tEnd);
  if (tr.startsolid || tr.fraction < 1) {
    resFlags &= ~(MantleFlag.OVER | MantleFlag.ON);
    endPos[0] = ledgePos[0]; endPos[1] = ledgePos[1]; endPos[2] = ledgePos[2];
    return;
  }
  tStart[0] = tEnd[0]; tStart[1] = tEnd[1]; tStart[2] = tEnd[2];
  tEnd[2] -= 18;
  pmTrace(pm, tr, tStart, mins, maxs, tEnd);
  if (tr.startsolid || tr.fraction < 1) {
    resFlags &= ~(MantleFlag.OVER | MantleFlag.ON);
    endPos[0] = ledgePos[0]; endPos[1] = ledgePos[1]; endPos[2] = ledgePos[2];
  } else {
    endPos[0] = tEnd[0]; endPos[1] = tEnd[1]; endPos[2] = tEnd[2];
  }
}

/** Mantle_CheckLedge [T5 bg_mantle.cpp]: is there a free ledge at `height`? Starts the mantle if JUMP is held. */
function checkLedge(pm: Pm, height: number): boolean {
  const ps = pm.ps;
  const mask = pm.tracemask & STANCE_MASK_CLEAR;
  mins[0] = -PLAYER_MAXS_STAND[0]; mins[1] = -PLAYER_MAXS_STAND[0]; mins[2] = 0;
  maxs[0] = PLAYER_MAXS_STAND[0]; maxs[1] = PLAYER_MAXS_STAND[0]; maxs[2] = 15 * 2;
  tStart[0] = startPos[0]; tStart[1] = startPos[1]; tStart[2] = startPos[2] + height;
  tEnd[0] = tStart[0] + 16 * mDir[0]; tEnd[1] = tStart[1] + 16 * mDir[1]; tEnd[2] = tStart[2] + 16 * mDir[2];
  pmTrace(pm, tr, tStart, mins, maxs, tEnd, mask);
  if (tr.startsolid || tr.fraction < 1) return false; // forward movement blocked
  tStart[0] = tEnd[0]; tStart[1] = tEnd[1]; tStart[2] = tEnd[2];
  tEnd[2] = startPos[2] + 18;
  pmTrace(pm, tr, tStart, mins, maxs, tEnd, mask);
  if (tr.startsolid || tr.fraction === 1) return false; // no ledge
  if (!traceWalkable(pm, tr)) return false; // ledge too steep
  ledgePos[0] = tEnd[0]; ledgePos[1] = tEnd[1];
  ledgePos[2] = (tEnd[2] - tStart[2]) * tr.fraction + tStart[2];
  if (ledgePos[2] - startPos[2] <= 0) return false;
  maxs[2] = PLAYER_HEIGHT_CROUCH;
  pmTrace(pm, tr, ledgePos, mins, maxs, ledgePos);
  if (tr.startsolid) return false; // can't fit crouched on the ledge
  tStart[0] = startPos[0]; tStart[1] = startPos[1]; tStart[2] = startPos[2] + height;
  const glassRange = MANTLE_CHECK_GLASS_EXTRA_RANGE + 16;
  tEnd[0] = tStart[0] + glassRange * mDir[0]; tEnd[1] = tStart[1] + glassRange * mDir[1]; tEnd[2] = tStart[2] + glassRange * mDir[2];
  maxs[2] = 15 * 2;
  pmTrace(pm, tr, tStart, mins, maxs, tEnd, mask);
  if (tr.startsolid || (tr.fraction < 1 && (tr.contents & CONTENTS.GLASS))) return false; // glass in the way
  if (!(resFlags & (MantleFlag.OVER | MantleFlag.ON))) return false;
  ps.mantleFlags |= MantleFlag.AVAILABLE;
  calcEndPos(pm);
  if (pm.cmd.buttons & Button.JUMP) {
    if (!(ps.pm_flags & PMF.DUCKED)) {
      pmTrace(pm, tr, ledgePos, PLAYER_MINS, PLAYER_MAXS_STAND, ledgePos);
      if (tr.startsolid) resFlags |= MantleFlag.CROUCH_AT_LEDGE;
      pmTrace(pm, tr, endPos, PLAYER_MINS, PLAYER_MAXS_STAND, endPos);
      if (!tr.startsolid) resFlags |= MantleFlag.STAND_AT_END;
    }
    mantleStart(pm);
  }
  return true;
}

/** Mantle_Start [T5 bg_mantle.cpp]. */
function mantleStart(pm: Pm): void {
  const ps = pm.ps;
  ps.mantleYaw = vecToYaw(mDir);
  ps.mantleTimer = 0;
  ps.mantleTransIndex = findTransition(startPos[2], ledgePos[2]);
  ps.mantleFlags = resFlags & 0xff;
  // Mantle_SetHaveWeapon (body lost in the T5 decompile): low mantles keep the weapon up (ESTIMATE)
  if (MANTLE_TRANS[ps.mantleTransIndex].height <= MANTLE_WEAPON_HEIGHT) ps.mantleFlags |= MantleFlag.KEEP_WEAPON;
  mantleAnimDelta(ps, mantleDuration(ps), trans);
  ps.origin[0] = endPos[0] - trans[0];
  ps.origin[1] = endPos[1] - trans[1];
  ps.origin[2] = endPos[2] - trans[2];
  ps.pm_flags |= PMF.MANTLE;
  ps.eFlags |= EF.MANTLING;
  pm.ctx.addEvent(ps, PEv.MANTLE, ps.mantleTransIndex | (ps.mantleFlags & MantleFlag.OVER ? 0x10 : 0));
}

/** Mantle_Check [T5 bg_mantle.cpp]: looks for a mantle every frame (sets the hint flag). */
export function mantleCheck(pm: Pm): void {
  const ps = pm.ps;
  if (!MANTLE_ENABLE) return;
  ps.mantleFlags &= ~MantleFlag.AVAILABLE;
  if (ps.pm_type >= PmType.FREEZE || ps.pm_type === PmType.DEAD || ps.pm_type === PmType.LASTSTAND) return;
  if (ps.pm_flags & PMF.MANTLE) return; // already mantling
  if (ps.pm_flags & (PMF.PRONE | PMF.DUCKED)) return; // must be standing
  if (!findMantleSurface(pm)) return;
  startPos[0] = ps.origin[0]; startPos[1] = ps.origin[1]; startPos[2] = ps.origin[2];
  if (!checkLedge(pm, 60) && !checkLedge(pm, 40)) checkLedge(pm, 20);
}

/** Mantle_Move [T5 bg_mantle.cpp]: follow the anim root motion; velocity = delta / dt. */
export function mantleMove(pm: Pm): void {
  const ps = pm.ps;
  ps.mantleFlags &= ~MantleFlag.AVAILABLE;
  if (ps.mantleFlags & MantleFlag.CROUCH_AT_LEDGE) ps.pm_flags |= PMF.DUCKED; // T5 event 9: force crouch
  const mantleLength = mantleDuration(ps);
  const prevTime = ps.mantleTimer;
  ps.mantleTimer = Math.min(mantleLength, ps.mantleTimer + pm.msec);
  const deltaTime = ps.mantleTimer - prevTime;
  mantleAnimDelta(ps, prevTime, prevTrans);
  mantleAnimDelta(ps, ps.mantleTimer, trans);
  const t0 = trans[0] - prevTrans[0], t1 = trans[1] - prevTrans[1], t2 = trans[2] - prevTrans[2];
  ps.origin[0] += t0; ps.origin[1] += t1; ps.origin[2] += t2;
  if (deltaTime > 0) {
    const inv = 1 / (deltaTime * 0.001);
    ps.velocity[0] = inv * t0; ps.velocity[1] = inv * t1; ps.velocity[2] = inv * t2;
  }
  ps.groundEntityNum = ENTITYNUM_NONE;
  if (ps.mantleTimer === mantleLength) {
    ps.pm_flags &= ~PMF.MANTLE;
    ps.eFlags &= ~EF.MANTLING;
    if (ps.mantleFlags & MantleFlag.STAND_AT_END) ps.pm_flags &= ~(PMF.DUCKED | PMF.PRONE); // T5 event 8: stand
  }
}

/** Mantle_CapView [T5 bg_mantle.cpp]: yaw limited to +-60 around the mantle direction. Returns the yaw delta applied. */
export function mantleCapView(ps: PlayerState): number {
  let delta = angleDelta(ps.mantleYaw, ps.viewangles[1]);
  const cap = MANTLE_VIEW_YAWCAP;
  if (delta >= -cap && delta <= cap) return 0;
  while (-cap > delta) delta += cap;
  while (delta > cap) delta -= cap;
  const v = delta <= 0 ? cap : -cap;
  ps.viewangles[1] = angleNorm360(ps.mantleYaw + v);
  return delta;
}

/** Mantle_IsWeaponInactive: the weapon is lowered for this mantle (weapons agent / viewmodel). */
export function mantleWeaponInactive(ps: PlayerState): boolean {
  return MANTLE_ENABLE && (ps.pm_flags & PMF.MANTLE) !== 0 && (ps.mantleFlags & MantleFlag.KEEP_WEAPON) === 0;
}

/** Mantle progress 0..1 (Mantle_GetMantleFrac), for the viewmodel/camera. */
export function mantleFrac(ps: PlayerState): number {
  const d = mantleDuration(ps);
  return d > 0 ? ps.mantleTimer / d : 0;
}

